import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { SupplyShedDriveTime } from '../../../lib/types';
import { getMillByCode } from '../../../lib/millsData';
import { usePerCrownStats } from '../../../hooks/usePerCrownStats';
import {
  usePerCrownNativeWindow,
  hasPerCrownCoverage,
  isPerCrownAvailable,
  PER_CROWN_AVAILABLE_MILL_CODE,
  PER_CROWN_AVAILABLE_DRIVE_TIME,
} from '../../../hooks/usePerCrownNativeWindow';
import { shortTons } from '../../../lib/units';
import {
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
  CHART_PRIMARY_COLOR,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
} from './contentPrimitives';

interface PerCrownContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
  // Native-resolution view state — when true, the map is painting the
  // per-crown vector polygons inside the demo window. Toggling flips both
  // visibility and (on the rising edge) flies the camera to the window
  // center; both are owned by DetailView.
  nativeViewActive: boolean;
  onToggleNativeView: () => void;
}

const fmtCount = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
};

const fmtTons = (mg: number): string => {
  const t = shortTons(mg);
  if (t >= 1e6) return `${(t / 1e6).toFixed(2)}M tons`;
  if (t >= 1e3) return `${(t / 1e3).toFixed(0)}K tons`;
  return `${t.toFixed(0)} tons`;
};

const DIAMETER_LABELS: Record<string, string> = {
  sapling: 'Sapling (<5″)',
  poletimber: 'Pole (5–9″)',
  small_sawtimber: 'Small saw (9–14″)',
  large_sawtimber: 'Large saw (14″+)',
};

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.tree_crown_segmentation} />
    <SkeletonPulseBlock className="h-[120px]" />
    <SkeletonPulseBlock className="h-[200px]" />
    <SkeletonPulseBlock className="h-[140px]" />
  </div>
);

// Empty-state panel rendered when the user picks the per-tree layer on a
// shed where v1.2 data isn't published. Discoverability is the goal: the
// layer stays in the dropdown for every mill so demo browsers can find it,
// and this panel offers a one-click jump to the only shed where it works.
const NoCoverageRedirectCard: React.FC = () => {
  const navigate = useNavigate();
  const targetMill = getMillByCode(PER_CROWN_AVAILABLE_MILL_CODE);

  const handleJump = () => {
    if (!targetMill) return;
    navigate(`/detail/${targetMill.id}`, {
      state: {
        mill: targetMill,
        layerId: 'tree_crown_segmentation',
        driveTime: PER_CROWN_AVAILABLE_DRIVE_TIME,
      },
    });
  };

  return (
    <div className="bg-figma-card rounded-figma-panel p-5 border border-figma-yellow/40 shadow-figma-card">
      <p className="text-xs font-roboto-mono text-figma-yellow uppercase tracking-wider">
        Demo coverage · Sierra Nevada only
      </p>
      <p className="mt-2 text-body-sm font-roboto text-neutral-300 leading-relaxed">
        Per-tree forest attributes are currently published for the
        California Sierra Nevada {PER_CROWN_AVAILABLE_DRIVE_TIME}-min supply
        shed only. The pilot dataset covers ~60M individual crown polygons
        across 64 NAIP tiles.
      </p>
      {targetMill && (
        <button
          type="button"
          onClick={handleJump}
          className="mt-4 w-full px-3 py-2 rounded-figma-panel font-roboto-mono text-xs uppercase tracking-wider bg-figma-yellow text-figma-panel hover:bg-figma-yellow/85 transition-colors"
        >
          View in California Sierra Nevada ({PER_CROWN_AVAILABLE_DRIVE_TIME} min) →
        </button>
      )}
    </div>
  );
};

export const PerCrownContent: React.FC<PerCrownContentProps> = ({
  millCode,
  driveTimeMinutes,
  nativeViewActive,
  onToggleNativeView,
}) => {
  const layer = DATA_LAYERS.tree_crown_segmentation;
  // Skipped on (mill, dt) tuples with no v1.2 publication so non-Sierra
  // sheds don't trigger a guaranteed 404 on every layer-open. When
  // disabled the hook returns `data: null, error: null` and the
  // NoCoverageRedirectCard below takes over.
  const { data, isLoading, error } = usePerCrownStats(
    millCode,
    driveTimeMinutes,
    isPerCrownAvailable(millCode, driveTimeMinutes as SupplyShedDriveTime),
  );
  // Independent fetch — `fetchWithCache` dedupes with the parallel call
  // inside DetailMapView so the GeoJSON only crosses the wire once.
  // The DataLayerId hook factory uses a generic `number` here while the
  // narrower hook expects SupplyShedDriveTime; runtime values are always
  // 60 or 90 so the cast is safe. Skipped on mills with no v1.2 publication
  // so non-Sierra sheds don't trigger a guaranteed 404 every layer-open.
  const nativeWindow = usePerCrownNativeWindow(
    millCode,
    driveTimeMinutes as SupplyShedDriveTime,
    hasPerCrownCoverage(millCode),
  );

  // Coverage check pulled up so the redirect CTA (when shown) renders at
  // the top of the panel — directly under the layer-picker dropdown the
  // user just interacted with — instead of sitting below the header +
  // About block. The actionable affordance for a non-Sierra mill should
  // be the first thing the user sees, with provenance / framing below.
  const showRedirect = !isLoading && !error && !data;

  return (
    <div className="space-y-6">
      {showRedirect && <NoCoverageRedirectCard />}

      {/* Native-window CTA hoisted above the LayerHeader so the actionable
          drill-down sits directly below the layer dropdown — same pattern
          as the no-coverage redirect. Provenance / framing follows below. */}
      {nativeWindow.data && (
        <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-yellow/40 shadow-figma-card">
          <p className="text-xs font-roboto-mono text-figma-yellow uppercase tracking-wider">
            Individual trees
            {nativeWindow.data.contextBoundary && (
              <span className="ml-1 text-neutral-400 normal-case tracking-normal">
                · {nativeWindow.data.contextBoundary.name}
              </span>
            )}
          </p>
          <p className="mt-2 text-body-sm font-roboto text-neutral-300 leading-relaxed">
            {nativeWindow.data.crownCount.toLocaleString()} crown polygons
            available at native 60 cm resolution
            {nativeWindow.data.contextBoundary
              ? ` inside ${nativeWindow.data.contextBoundary.name}`
              : ' in the densest 1 km² of the shed'}
            .{' '}
            <span className="md:hidden">Pinch to zoom in, then tap any tree to see its attributes.</span>
            <span className="hidden md:inline">Hover any tree on the map to see its attributes.</span>
          </p>
          <button
            type="button"
            onClick={onToggleNativeView}
            className={`mt-3 w-full px-3 py-2 rounded-figma-panel font-roboto-mono text-xs uppercase tracking-wider transition-colors ${
              nativeViewActive
                ? 'bg-figma-card border border-figma-yellow text-figma-yellow hover:bg-figma-yellow/10'
                : 'bg-figma-yellow text-figma-panel hover:bg-figma-yellow/85'
            }`}
          >
            {nativeViewActive ? 'Exit native resolution view' : 'View individual trees →'}
          </button>
        </div>
      )}

      <LayerHeader layer={layer} />

      {/* Pilot-preview ribbon. The per-crown CSV is real data (not a stub
          like the per-tree insight-card rollup JSON), but it's still the
          early pilot release: Sierra Nevada 60-min only, DBH biased low
          for old growth, fit_level=5 crowns excluded from the headline
          count. Yellow chrome matches the figma-yellow Pilot-preview cue
          family used by the insight-card stubLabel and the per-tree CTA
          tile — so users register the layer as "preview, not production". */}
      <div className="rounded border border-figma-yellow/40 bg-figma-yellow/5 px-3 py-2">
        <p className="text-[11px] font-roboto-mono uppercase tracking-wider text-figma-yellow font-bold">
          Pilot preview
        </p>
        <p className="mt-1 text-xs font-roboto text-neutral-300 leading-relaxed">
          California Sierra Nevada 60-min shed only.
        </p>
      </div>

      <AboutLayer>
        <p>
          A <strong>CTrees / TreeMap / NSVB</strong> pipeline. CTrees
          delineated each tree crown from 60&thinsp;cm NAIP aerial imagery
          (Bey et al.) and built canopy height models (CHMs) off the same
          imagery. USFS{' '}
          <GlossaryTerm term="treemap">TreeMap</GlossaryTerm>{' '}
          provides per-pixel forest-type and stand attribution by imputing
          FIA plot data onto a 30 m raster, used to stratify each crown
          into the right{' '}
          <GlossaryTerm term="nsvb">NSVB</GlossaryTerm> allometric class.
          USFS NSVB coefficients were then applied to estimate Total Tree
          Biomass, NSVB component partitions (bole, bark, branch, foliage,
          stump, root), merchantable volume, growing-stock volume, and a{' '}
          <GlossaryTerm term="dbh">DBH</GlossaryTerm> estimate per crown.
        </p>
        <p>
          <strong>Total Tree Biomass</strong> includes coarse-root biomass
          (~18% of total) — it is not <GlossaryTerm term="agb">above-ground biomass</GlossaryTerm>.
          The left panel surfaces both the total and an AGB-only estimate
          (<code className="px-1 bg-figma-panel rounded">AGB_kg − root_kg</code>)
          so the bottom-up total is comparable to top-down satellite AGB.
        </p>
        <p>
          <strong>Caveats.</strong> DBH and merchantable volume are biased low
          for old-growth — DBH saturates around 32–44 cm even for 60+ m trees.
          Mass columns are crown-area + height driven and are not affected.
          Crowns whose allometric cascade fell to the spatial fallback level
          (fit_level = 5, ~25% of crowns) sit on marginal/non-forest pixels
          and are excluded from the headline tree count.
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {/* The no-coverage redirect now renders at the top of the panel
          (see `showRedirect` above). When the redirect path is taken
          there's no per-shed data to show further below. */}

      {!isLoading && data && (() => {
        const fitLevel5Pct =
          data.treeCountTotal > 0
            ? (data.fitLevel5Count / data.treeCountTotal) * 100
            : 0;
        const sawtimberPct =
          data.treeCountTotal > 0
            ? (data.sawtimberCount / data.treeCountTotal) * 100
            : 0;

        const diameterChart = data.diameterClasses.map((d) => ({
          label: DIAMETER_LABELS[d.cls] ?? d.cls,
          count: d.count,
          biomassMg: d.totalTreeBiomassMg,
        }));

        const topForestTypes = data.forestTypes.slice(0, 5);
        const totalForestTypeCount = data.forestTypes.reduce(
          (s, ft) => s + ft.count,
          0,
        );

        return (
          <>
            <LayerLiveBadge />

            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
                Individual trees in this supply shed
              </p>
              <p className="mt-3 text-4xl font-roboto-mono font-bold text-figma-yellow leading-none">
                {fmtCount(data.treeCount)}
              </p>
              <p className="mt-1 text-body-sm font-roboto text-neutral-300">
                crowns delineated and attributed
                {data.treeCountTotal > data.treeCount && (
                  <>
                    {' '}
                    <span className="text-neutral-500">
                      ({fmtCount(data.treeCountTotal - data.treeCount)} non-forest fallback excluded)
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Total Tree Biomass"
                value={fmtTons(data.totalTreeBiomassMg)}
                info="Sum of AGB_kg across crowns, displayed in US short tons. Includes coarse-root biomass — not above-ground only."
              />
              <StatCard
                label="Above-Ground Biomass"
                value={fmtTons(data.aboveGroundBiomassMg)}
                info="AGB_kg − root_kg summed across crowns, in short tons. Apples-to-apples with the AGB 100m raster."
              />
              <StatCard
                label="Sawtimber trees"
                value={`${fmtCount(data.sawtimberCount)} (${sawtimberPct.toFixed(1)}%)`}
                info="Crowns with estimated DBH ≥ 22.9 cm (small_saw + large_saw). Note: DBH is biased low for old-growth, so this fraction is conservative."
              />
              <StatCard
                label="Avg tree size"
                value={
                  data.treeCount > 0
                    ? `${(shortTons(data.totalTreeBiomassMg) / data.treeCount).toFixed(2)} tons`
                    : '—'
                }
                info="Total Tree Biomass ÷ tree count. Boundary-independent — gives a feel for the typical tree's mass across the shed."
              />
            </div>

            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <h4 className="text-base font-roboto font-bold text-figma-text mb-1">
                Tree count by diameter class
              </h4>
              <p className="text-xs font-roboto text-neutral-500 mb-3">
                FIA class boundaries: sapling &lt;5″, pole 5–9″, small saw 9–14″, large saw 14″+
              </p>
              <ResponsiveContainer width="100%" height={200} minWidth={0}>
                <BarChart
                  data={diameterChart}
                  margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    tickMargin={6}
                  />
                  <YAxis
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    width={48}
                    tickFormatter={(v: number) => fmtCount(v)}
                  />
                  <Tooltip
                    cursor={false}
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                    itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                    formatter={(value: number | undefined, _name: string | undefined, props: { payload?: { biomassMg?: number } }) => {
                      const count = value ?? 0;
                      const biomass = props.payload?.biomassMg ?? 0;
                      return [
                        `${fmtCount(count)} trees · ${fmtTons(biomass)}`,
                        'In this class',
                      ];
                    }}
                  />
                  <Bar dataKey="count" isAnimationActive={false}>
                    {diameterChart.map((d) => (
                      <Cell key={d.label} fill={CHART_PRIMARY_COLOR} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
                DBH is estimated from height and crown area and saturates ~32–44 cm
                even for 60+ m trees, so the large-saw bar is a conservative floor.
              </p>
            </div>

            {topForestTypes.length > 0 && (
              <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
                <h4 className="text-base font-roboto font-bold text-figma-text mb-1">
                  Top FIA forest types
                </h4>
                <p className="text-xs font-roboto text-neutral-500 mb-3">
                  Share of attributed crowns by forest-type code
                </p>
                <div className="space-y-2">
                  {topForestTypes.map((ft) => {
                    const pct =
                      totalForestTypeCount > 0
                        ? (ft.count / totalForestTypeCount) * 100
                        : 0;
                    return (
                      <div key={ft.fortypcd} className="flex items-baseline gap-3">
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-roboto text-figma-text">
                              {ft.name}
                            </span>
                            <span className="text-xs font-roboto-mono text-neutral-400">
                              {fmtCount(ft.count)} · {pct.toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-1 bg-figma-surface rounded mt-1 overflow-hidden">
                            <div
                              className="h-full bg-figma-lime/70"
                              style={{ width: `${pct.toFixed(1)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {fitLevel5Pct > 0 && (
              <p className="text-xs font-roboto italic text-neutral-500 leading-relaxed">
                {fitLevel5Pct.toFixed(0)}% of crowns sit on pixels where the
                allometric cascade fell to the spatial fallback (fit_level = 5)
                — typically marginal / non-forest. They appear on the map but
                are excluded from the headline count above.
              </p>
            )}
          </>
        );
      })()}
    </div>
  );
};
